import React, {
  type ChangeEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { FormGroup, Label, Input, FormFeedback, FormText } from "reactstrap";
import { useField } from "formik";
import HelpPopover from "./HelpPopover";
import { countCharacters } from "~/utils/priority-messages";

type Value = string | number | string[] | undefined;
export type ColSize =
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10"
  | "11"
  | "12";

interface Props {
  col?: ColSize;
  name: string;
  type?:
    | "number"
    | "text"
    | "tel"
    | "url"
    | "email"
    | "search"
    | "password"
    | "date"
    | "datetime-local"
    | "textarea";
  placeholder?: string;
  label?: string;
  recommendedValues?: string[];
  help?: ReactNode;
  disabled?: boolean;
  nullable?: boolean;
  maxLength?: number;
  min?: number;
  max?: number;
  description?: string | ReactNode;
  characterLimit?: number;
  rows?: number;
  autoGrow?: boolean;
}

export default function TextField<V extends Value>(props: Props) {
  const [field, meta, helpers] = useField<V | null>(props.name);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.value === "" && props.nullable) {
        helpers.setValue(null);
        return;
      }
      field.onChange(e);
    },
    [field, helpers, props.nullable]
  );

  const isTextarea = props.type === "textarea";
  const autoGrow = isTextarea && props.autoGrow;
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = inputRef.current;
    if (!autoGrow || !textarea) return;

    const fit = () => {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    };

    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [autoGrow, field.value]);

  const invalid = Boolean(meta.touched && meta.error);

  const prefix = field.name.replaceAll(".", "-");

  let listId: string | undefined;
  if (props.recommendedValues) {
    listId = prefix + "-datalist";
  }

  const showCount = props.characterLimit !== undefined;
  const characterCount = showCount
    ? countCharacters(String(field.value ?? ""))
    : 0;
  const overLimit = showCount && characterCount > Number(props.characterLimit);

  return (
    <FormGroup className={props.col && `col-md-${props.col}`}>
      {props.label && <Label for={field.name}>{props.label}</Label>}
      <HelpPopover id={field.name + "-help"} text={props.help} />
      <Input
        {...field}
        value={field.value ?? ""}
        id={field.name}
        onChange={onChange}
        invalid={invalid}
        type={props.type}
        list={listId}
        placeholder={props.placeholder}
        disabled={props.disabled}
        maxLength={props.maxLength}
        min={props.min}
        max={props.max}
        rows={isTextarea ? props.rows : undefined}
        innerRef={autoGrow ? inputRef : undefined}
        style={autoGrow ? { overflowY: "hidden", resize: "none" } : undefined}
      />
      {props.recommendedValues && (
        <datalist id={listId}>
          {props.recommendedValues.map((v) => (
            <option key={v} value={v} />
          ))}
        </datalist>
      )}
      <FormFeedback>{meta.error}</FormFeedback>
      {showCount && (
        <FormText
          className={overLimit ? "text-danger float-right" : "float-right"}
        >
          {characterCount}/{props.characterLimit}
        </FormText>
      )}
      <FormText>{props.description}</FormText>
    </FormGroup>
  );
}
